import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import ReleaseAnnouncementModal from './components/ReleaseAnnouncementModal.vue';
import { PaidFeaturesApi, PaidTariffModalType } from '@/api/PaidFeaturesApi';
import ProExpiringModal from './components/ProExpiringModal.vue';
import TrialExpiringModal from './components/TrialExpiringModal.vue';
import PauseEndingSoonModal from './components/PauseEndingSoonModal.vue';
import PauseResumedModal from './components/PauseResumedModal.vue';
import PauseFinalChanceModal from './components/PauseFinalChanceModal.vue';
import PauseProModal from './components/PauseProModal.vue';
import ResumeImportantModal from '@/pages/Subscription/components/ResumeImportantModal.vue';
import TrialExpiredModal from './components/TrialExpiredModal.vue';
import { UsersApi } from '@/api/UsersApi';
import { TeacherApi } from '@/api/TeacherApi';
import { UserInfoModel } from '@/types/UserInfoModel';
import { SubscriptionApi } from '@/api/SubscriptionApi';
import { useToast } from 'vue-toastification';
import { PaymentsApi } from '@/api/PaymentsApi';
import TryTrialModal from './components/TryTrialModal.vue';
import UpsellAfterCancelModals from '../UpsellAfterCancelModals/UpsellAfterCancelModals.vue';
import PackagesAnnouncementModals from '../PackagesAnnouncementModals/PackagesAnnouncementModals.vue';
import ServiceOutageModals from '../ServiceOutageModals/ServiceOutageModals.vue';
import ServiceRecoveryModals from '../ServiceRecoveryModals/ServiceRecoveryModals.vue';

type PauseFeature = 'calls' | 'board' | 'students';

interface PauseProModalShowDetail {
    feature: PauseFeature;
    pauseReturnDeadline: string | null;
    isEmbedded?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TariffNotificationModals',
  setup(__props) {

const toast = useToast();

const isPaymentProcess = ref(false);
const modalType = ref<PaidTariffModalType | null>(null);
const proExpiresAt = ref<null | string>(null);
const freeAccessUntil = ref<null | string>(null);
const isTrialExpired = ref(false);
const trialDaysRemaining = ref<null | number>(null);
const pauseEndsAt = ref<null | string>(null);
const transferToFreeAt = ref<null | string>(null);
const pauseFeature = ref<PauseFeature | null>(null);
const pauseReturnDeadline = ref<string | null>(null);
const isPauseResumeRequested = ref(false);
const pauseShowNavigate = ref(true);
const pauseIsEmbedded = ref(false);

// Модалка о сбое (FINCH-2630) важнее остальных модалок при входе: тарифные, апселл и анонс
// пакетов запрашиваются, только когда её проверка завершилась и показывать её не нужно.
// Если она открыта — остальные придут на следующей загрузке страницы после её закрытия,
// а не стопкой поверх. Действует в пределах одной страницы: во вложенном фрейме (доска
// внутри звонка) модалка о сбое пропускается, и остальные модалки там показываются как раньше.
const otherEntryModalsAllowed = ref(false);

// Модалка «Восстанавливаем работу Finch» (FINCH-2631) — следующая в цепочке после модалки о сбое:
// её хост монтируется после checked(false) от модалки о сбое, а остальные модалки входа ждут уже её checked(false).
const serviceRecoveryAllowed = ref(false);

const releaseAnnouncementModal = ref<InstanceType<typeof ReleaseAnnouncementModal> | null>(null);
const proExpiringModal = ref<InstanceType<typeof ProExpiringModal> | null>(null);
const paymentFailedModal = ref<InstanceType<typeof ProExpiringModal> | null>(null);
const trialExpiringModal = ref<InstanceType<typeof TrialExpiringModal> | null>(null);
const pauseProModal = ref<InstanceType<typeof PauseProModal> | null>(null);
const pauseEndingSoonModal = ref<InstanceType<typeof PauseEndingSoonModal> | null>(null);
const pauseResumedModal = ref<InstanceType<typeof PauseResumedModal> | null>(null);
const pauseFinalChanceModal = ref<InstanceType<typeof PauseFinalChanceModal> | null>(null);
const resumeImportantModal = ref<InstanceType<typeof ResumeImportantModal> | null>(null);

function onPauseProShow(event: Event) {
    const customEvent = event as CustomEvent<PauseProModalShowDetail>;
    pauseFeature.value = customEvent.detail.feature;
    pauseReturnDeadline.value = customEvent.detail.pauseReturnDeadline;
    isPauseResumeRequested.value = false;
    
    // Determine if we should navigate on close:
    // - Don't navigate if it's StudentCards
    // - Don't navigate if it's embedded board (in iframe)
    // - Do navigate for calls and standalone board
    const isEmbeddedBoard = customEvent.detail.isEmbedded === true;
    pauseIsEmbedded.value = isEmbeddedBoard;
    pauseShowNavigate.value = customEvent.detail.feature !== 'students' && !isEmbeddedBoard;
    
    nextTick(() => {
        pauseProModal.value?.show();
    });
}

function onPauseCancel() {
    isPauseResumeRequested.value = false;
    pauseProModal.value?.hide();
}

function onPauseResume() {
    isPauseResumeRequested.value = true;
    pauseProModal.value?.hide();
}

function navigateBackOrHome() {
    if (window.history.length > 1) {
        window.history.back();
        return;
    }

    window.location.href = '/';
}

function onPauseHidden() {
    if (isPauseResumeRequested.value) {
        isPauseResumeRequested.value = false;
        nextTick(() => {
            resumeImportantModal.value?.show();
        });
        return;
    }

    if (pauseShowNavigate.value) {
        navigateBackOrHome();
        return;
    }

    // Embedded board inside call: close board instead of navigation.
    if (pauseFeature.value === 'board' && pauseIsEmbedded.value) {
        window.postMessage({ type: 'close-board' }, '*');
    }
}

function getPauseFlag(entity: unknown): boolean {
    const data = entity as { isOnPause?: boolean; IsOnPause?: boolean };
    return Boolean(data?.isOnPause ?? data?.IsOnPause ?? false);
}

function hasStudentsAccess(entity: unknown): boolean {
    const data = entity as {
        hasProTariff?: boolean;
        hasUnlimitedStudents?: boolean;
        studentCardsCount?: number | null;
        baseTariffMaxStudentCount?: number | null;
    };

    if (data?.hasProTariff || data?.hasUnlimitedStudents) {
        return true;
    }

    return Number(data?.studentCardsCount) < Number(data?.baseTariffMaxStudentCount);
}

function getPauseDeadline(entity: unknown): string | null {
    const data = entity as { pauseReturnDeadline?: string | null; PauseReturnDeadline?: string | null };
    return data?.pauseReturnDeadline ?? data?.PauseReturnDeadline ?? null;
}

function showStudentLimitModal() {
    const limitModal = document.getElementById('StudentLimitWarningModal');
    if (limitModal) {
        const bootstrapWindow = window as Window & {
            bootstrap?: {
                Modal: new (element: Element) => { show: () => void };
            };
        };
        bootstrapWindow.bootstrap?.Modal && new bootstrapWindow.bootstrap.Modal(limitModal).show();
    }
}

function interceptLegacyStudentLimitModal(event: Event) {
    const target = event.target;
    const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
    const trigger = element?.closest('[data-bs-target="#StudentLimitWarningModal"], [data-bs-target="#StudentAlmostLimitWarningModal"]');

    if (!trigger) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    new TeacherApi().getCurrentTeacherTariffInfo()
        .then((result) => {
            if (result.isSuccess && !hasStudentsAccess(result.entity) && getPauseFlag(result.entity)) {
                window.dispatchEvent(new CustomEvent('pause-pro-modal:show', {
                    detail: {
                        feature: 'students',
                        pauseReturnDeadline: getPauseDeadline(result.entity),
                    },
                }));
                return;
            }

            showStudentLimitModal();
        })
        .catch((e) => {
            console.error(e);
            showStudentLimitModal();
        });
}

async function onResumeConfirm() {
    let userData: UserInfoModel | null = null;
    try {
        const result = await new UsersApi().getCurrentInfo();
        if (result.isSuccess) {
            userData = result.entity;
        }
    } catch (e) {
        console.error(e);
    }

    if (!userData) {
        return;
    }

    try {
        const result = await new SubscriptionApi().resume(userData.id);
        if (result.isSuccess) {
            window.location.reload();
        } else {
            throw errorsListToString(result.errors);
        }
    } catch (e) {
        console.error(e);
        navigateBackOrHome();
    }
}

function errorsListToString(errors: { key: string; value: string }[]): string {
    return errors.map(err => `${err.key}: ${err.value}`).join('\n');
}
const tryTrialModal = ref<InstanceType<typeof TryTrialModal> | null>(null);
const trialExpiredModal = ref<InstanceType<typeof TrialExpiredModal> | null>(null);

const dismissed = new Set<PaidTariffModalType>();

async function dismissModal(type: PaidTariffModalType) {
    if (dismissed.has(type)) return;
    dismissed.add(type);
    try {
        return await new PaidFeaturesApi().dismissNewTariffModal(type);
    } catch (e) {
        console.error(e);
    }
}

async function onDetails() {
    await dismissModal(PaidTariffModalType.ProExpiring);
    window.location.href = '/Subscription';
}

async function onConfirmTryTrial() {
    await dismissModal(PaidTariffModalType.TryTrial);
    window.location.href = '/PaidFeatures';
}

async function onConfirmTrialExpired() {
    await dismissModal(PaidTariffModalType.TrialExpired);
    window.location.href = '/PaidFeatures';
}

async function onConfirmProExpiring() {
    await dismissModal(PaidTariffModalType.ProExpiring);
    let userData: UserInfoModel | null = null;
    try {
        const result = await new UsersApi().getCurrentInfo();
        if (result.isSuccess) {
            userData = result.entity;
        }
        if (!userData) {
            throw new Error('userData is not defined');
        }
    } catch (e) {
        console.error(e);
        return;
    }
    try {
        const result = await new SubscriptionApi().restore(userData.id);
        if (result.isSuccess) {
            window.location.href = '/Subscription';
        } else {
            console.error(result.errors);
        }
    } catch(e) {
        console.error(e);
    }
}

async function retryPayment(userData: UserInfoModel): Promise<number | null> {
    if (!userData) {
        return null;
    }
    const result = await new PaymentsApi().retryPayment(userData?.id);
    if (result.isSuccess) {
        return result.entity;
    } else {
        toast.error(result.errors.map(err => `${err.key}: ${err.value}`).join('\n'));
        return null;
    }
}

async function onConfirmProPaymentFailed() {
    await dismissModal(PaidTariffModalType.ProPaymentFailed);
    let userData: UserInfoModel | null = null;
    try {
        const result = await new UsersApi().getCurrentInfo();
        if (result.isSuccess) {
            userData = result.entity;
        }
        if (!userData) {
            throw new Error('userData is not defined');
        }
    } catch (e) {
        console.error(e);
        navigateBackOrHome();
        return;
    }
    isPaymentProcess.value = true;
    const paymentId = await retryPayment(userData);
    if (!paymentId) {
        isPaymentProcess.value = false;
        navigateBackOrHome();
        return;
    }
    // Списание прошло синхронно; продление PRO подтвердит вебхук (~1с) — обновляем страницу.
    setTimeout(() => window.location.reload(), 2000);
}

async function onConfirm(type: PaidTariffModalType) {
    await dismissModal(type);
    window.location.href = '/Subscription';
}

// Все CTA групп 1 и 3 (оплатить/отменить/подробнее) ведут в раздел «Подписка».
// Гасим именно показанный повод (за 7д/1д; день окончания/конец grace) — тип пришёл с бэка в modalType.
async function onPauseEndingSoonAction() {
    if (modalType.value != null) {
        await dismissModal(modalType.value);
    }
    window.location.href = '/Subscription';
}

async function onPauseFinalChanceAction() {
    if (modalType.value != null) {
        await dismissModal(modalType.value);
    }
    window.location.href = '/Subscription';
}

// Закрытие без CTA (крестик) — помечаем показанный повод, но без перехода.
async function onPauseEndingSoonHidden() {
    if (modalType.value != null) {
        await dismissModal(modalType.value);
    }
}

async function onPauseFinalChanceHidden() {
    if (modalType.value != null) {
        await dismissModal(modalType.value);
    }
}

function onServiceOutageChecked(shown: boolean) {
    if (shown) {
        return;
    }
    serviceRecoveryAllowed.value = true;
}

async function onServiceRecoveryChecked(shown: boolean) {
    if (shown) {
        return;
    }
    otherEntryModalsAllowed.value = true;
    await showPaidTariffModal();
}

onMounted(() => {
    // Прослушка события с доски для показа модалки паузы ПРО при попытке подключить студентов сверх лимита на базовом тарифе или при попытке подключить доску внутри другого приложения (например, внутри звонка).
    window.addEventListener('pause-pro-modal:show', onPauseProShow as EventListener);
    document.addEventListener('click', interceptLegacyStudentLimitModal, true);
});

async function showPaidTariffModal() {
    const modalInfo = await new PaidFeaturesApi().getModalInfo();

    if (!modalInfo.entity?.shouldShow || !modalInfo.entity.modalType) {
        return;
    }

    modalType.value = modalInfo.entity.modalType;

    if (modalInfo.entity.proExpiresAt) {
        proExpiresAt.value = modalInfo.entity.proExpiresAt;
    }
    if (modalInfo.entity.freeAccessUntil) {
        freeAccessUntil.value = modalInfo.entity.freeAccessUntil;
    }
    if (modalInfo.entity.isTrialExpired) {
        isTrialExpired.value = modalInfo.entity.isTrialExpired;
    }
    if (modalInfo.entity.trialDaysRemaining) {
        trialDaysRemaining.value = modalInfo.entity.trialDaysRemaining;
    }
    if (modalInfo.entity.pauseEndsAt) {
        pauseEndsAt.value = modalInfo.entity.pauseEndsAt;
    }
    if (modalInfo.entity.transferToFreeAt) {
        transferToFreeAt.value = modalInfo.entity.transferToFreeAt;
    }

    await nextTick();

    if (modalType.value === PaidTariffModalType.ReleaseAnnouncement) {
        releaseAnnouncementModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.ProExpiring) {
        proExpiringModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.ProPaymentFailed) {
        paymentFailedModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.TrialExpiring) {
        trialExpiringModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.TryTrial) {
        tryTrialModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.TrialExpired) {
        trialExpiredModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.PauseEndingSoon
        || modalType.value === PaidTariffModalType.PauseEndingSoonLastDay) {
        pauseEndingSoonModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.PauseResumed) {
        pauseResumedModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.PauseFinalChance
        || modalType.value === PaidTariffModalType.PauseGraceEnding) {
        pauseFinalChanceModal.value?.show();
    }
}

onBeforeUnmount(() => {
    window.removeEventListener('pause-pro-modal:show', onPauseProShow as EventListener);
    document.removeEventListener('click', interceptLegacyStudentLimitModal, true);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (freeAccessUntil.value)
      ? (_openBlock(), _createBlock(ReleaseAnnouncementModal, {
          key: 0,
          onConfirm: _cache[0] || (_cache[0] = ($event: any) => (dismissModal(_unref(PaidTariffModalType).ReleaseAnnouncement))),
          onHidden: _cache[1] || (_cache[1] = ($event: any) => (dismissModal(_unref(PaidTariffModalType).ReleaseAnnouncement))),
          ref_key: "releaseAnnouncementModal",
          ref: releaseAnnouncementModal,
          "free-access-until": freeAccessUntil.value,
          "is-trial-expired": isTrialExpired.value
        }, null, 8, ["free-access-until", "is-trial-expired"]))
      : _createCommentVNode("", true),
    (proExpiresAt.value)
      ? (_openBlock(), _createBlock(ProExpiringModal, {
          key: 1,
          "payment-failed": false,
          onDetails: onDetails,
          onConfirm: onConfirmProExpiring,
          onHidden: _cache[2] || (_cache[2] = ($event: any) => (dismissModal(_unref(PaidTariffModalType).ProExpiring))),
          ref_key: "proExpiringModal",
          ref: proExpiringModal,
          "pro-expires-at": proExpiresAt.value
        }, null, 8, ["pro-expires-at"]))
      : _createCommentVNode("", true),
    (proExpiresAt.value)
      ? (_openBlock(), _createBlock(ProExpiringModal, {
          key: 2,
          "payment-failed": true,
          onDetails: onDetails,
          onConfirm: onConfirmProPaymentFailed,
          onHidden: _cache[3] || (_cache[3] = ($event: any) => (dismissModal(_unref(PaidTariffModalType).ProPaymentFailed))),
          ref_key: "paymentFailedModal",
          ref: paymentFailedModal,
          "pro-expires-at": proExpiresAt.value
        }, null, 8, ["pro-expires-at"]))
      : _createCommentVNode("", true),
    (trialDaysRemaining.value != null)
      ? (_openBlock(), _createBlock(TrialExpiringModal, {
          key: 3,
          ref_key: "trialExpiringModal",
          ref: trialExpiringModal,
          onConfirm: _cache[4] || (_cache[4] = ($event: any) => (onConfirm(_unref(PaidTariffModalType).TrialExpiring))),
          onHidden: _cache[5] || (_cache[5] = ($event: any) => (dismissModal(_unref(PaidTariffModalType).TrialExpiring)))
        }, null, 512))
      : _createCommentVNode("", true),
    (pauseFeature.value)
      ? (_openBlock(), _createBlock(PauseProModal, {
          key: 4,
          ref_key: "pauseProModal",
          ref: pauseProModal,
          feature: pauseFeature.value,
          "pause-return-deadline": pauseReturnDeadline.value,
          "show-navigate-on-close": pauseShowNavigate.value,
          onResume: onPauseResume,
          onCancel: onPauseCancel,
          onHidden: onPauseHidden
        }, null, 8, ["feature", "pause-return-deadline", "show-navigate-on-close"]))
      : _createCommentVNode("", true),
    (pauseEndsAt.value)
      ? (_openBlock(), _createBlock(PauseEndingSoonModal, {
          key: 5,
          ref_key: "pauseEndingSoonModal",
          ref: pauseEndingSoonModal,
          "pause-ends-at": pauseEndsAt.value,
          onConfirm: onPauseEndingSoonAction,
          onSecondary: onPauseEndingSoonAction,
          onHidden: onPauseEndingSoonHidden
        }, null, 8, ["pause-ends-at"]))
      : _createCommentVNode("", true),
    _createVNode(PauseResumedModal, {
      ref_key: "pauseResumedModal",
      ref: pauseResumedModal,
      onHidden: _cache[6] || (_cache[6] = ($event: any) => (dismissModal(_unref(PaidTariffModalType).PauseResumed)))
    }, null, 512),
    (transferToFreeAt.value)
      ? (_openBlock(), _createBlock(PauseFinalChanceModal, {
          key: 6,
          ref_key: "pauseFinalChanceModal",
          ref: pauseFinalChanceModal,
          "transfer-to-free-at": transferToFreeAt.value,
          onConfirm: onPauseFinalChanceAction,
          onSecondary: onPauseFinalChanceAction,
          onHidden: onPauseFinalChanceHidden
        }, null, 8, ["transfer-to-free-at"]))
      : _createCommentVNode("", true),
    _createVNode(ResumeImportantModal, {
      ref_key: "resumeImportantModal",
      ref: resumeImportantModal,
      "has-active-paid-period": true,
      onResume: onResumeConfirm,
      onCancel: navigateBackOrHome
    }, null, 512),
    _createVNode(TryTrialModal, {
      onHidden: _cache[7] || (_cache[7] = ($event: any) => (dismissModal(_unref(PaidTariffModalType).TryTrial))),
      onConfirm: onConfirmTryTrial,
      ref_key: "tryTrialModal",
      ref: tryTrialModal
    }, null, 512),
    (modalType.value === _unref(PaidTariffModalType).TrialExpired)
      ? (_openBlock(), _createBlock(TrialExpiredModal, {
          key: 7,
          ref_key: "trialExpiredModal",
          ref: trialExpiredModal,
          onConfirm: onConfirmTrialExpired,
          onHidden: _cache[8] || (_cache[8] = ($event: any) => (dismissModal(_unref(PaidTariffModalType).TrialExpired)))
        }, null, 512))
      : _createCommentVNode("", true),
    (otherEntryModalsAllowed.value)
      ? (_openBlock(), _createBlock(UpsellAfterCancelModals, { key: 8 }))
      : _createCommentVNode("", true),
    (otherEntryModalsAllowed.value)
      ? (_openBlock(), _createBlock(PackagesAnnouncementModals, { key: 9 }))
      : _createCommentVNode("", true),
    _createVNode(ServiceOutageModals, { onChecked: onServiceOutageChecked }),
    (serviceRecoveryAllowed.value)
      ? (_openBlock(), _createBlock(ServiceRecoveryModals, {
          key: 10,
          onChecked: onServiceRecoveryChecked
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})